/**
 * The `rooms/{roomId}` document, as the data layer admits it.
 *
 * Lenient like the structure schema, and for the same reason: a room carries
 * forty-odd pipeline fields (`referenceOriginTransform`, `centerClearance`,
 * `rakeEdges`, …) and nobody is served by refusing a real room over one of
 * them. Strict on what every reader depends on: the room's id and
 * `structureId`, and an `id` on every element of the arrays the operation
 * writer addresses.
 *
 * Measured on the emulator corpus (340 rooms, 2026-10-22): `objects`, `walls`,
 * `floors`, `ceilings`, `doors`, `windows`, `openings` and `cabinetRuns` carry
 * a string `id` on every element; `sections` has one element without. The
 * schema types the first set as id'd element arrays and leaves the rest to
 * pass through — the save planner decides per field, at write time, whether
 * an array can be written as operations (see `roomSave.ts`).
 *
 * `objects[].type` is optional: one corpus object has none, and a reader
 * that needs it already handles its absence.
 *
 * EVERY object shape here is `.passthrough()`, the triples included. A plain
 * `z.object` STRIPS the keys it does not name, and the parsed copy is what the
 * room store holds and what its save upserts back — so a stripped key is
 * drawn wrong and then written away. `dimensions` is the one that carried
 * them: a corner upper's `isUpper` / `cornerBox` / `alongA` / `alongB`, a
 * sink's cutout sizes, a surround's `sidePanelThickness` (#2886).
 * `schemaPassthrough.ci.test.ts` refuses a shape that strips.
 */
import { z } from 'zod';

const Vec3 = z
  .object({ x: z.number(), y: z.number(), z: z.number() })
  .passthrough();

/** Anything the operation writer can address: an object with a string id. */
export const IdElementSchema = z
  .object({ id: z.string().min(1) })
  .passthrough();

export const RoomObjectSchema = z
  .object({
    id: z.string().min(1),
    type: z.string().optional(),
    center: Vec3.optional(),
    dimensions: Vec3.optional(),
    rotation: Vec3.optional(),
    parentId: z.string().nullable().optional(),
  })
  .passthrough();

/** A room's SLIM copy of a canonical opening (the geometry lives on the structure). */
export const RoomOpeningCopySchema = z
  .object({
    id: z.string().min(1),
    structureOpeningId: z.string().optional(),
    parentWallIds: z.array(z.string()).optional(),
  })
  .passthrough();

export const RoomDocSchema = z
  .object({
    id: z.string().min(1),
    structureId: z.string().min(1),
    name: z.string().optional(),
    story: z.number().nullable().optional(),
    objects: z.array(RoomObjectSchema).default([]),
    walls: z.array(IdElementSchema).default([]),
    floors: z.array(IdElementSchema).default([]),
    ceilings: z.array(IdElementSchema).optional(),
    doors: z.array(RoomOpeningCopySchema).default([]),
    windows: z.array(RoomOpeningCopySchema).default([]),
    openings: z.array(RoomOpeningCopySchema).default([]),
    cabinetRuns: z.array(IdElementSchema).optional(),
  })
  .passthrough();
